import DayDivider from 'components/timekeeping/day_divider'
import { UnknownContactPanel } from 'components/timelines/items/unknown_contact_panel'
import { UnknownTopicPanel } from 'components/timelines/items/unknown_topic_panel'
import { SpamOptionsDialog } from 'components/timelines/spam_options_dialog'
import { useUnknownContact } from 'components/timelines/use_unknown_contact'
import ErrorBoundary from 'containers/error_boundary'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom/client'
import { useDispatch, useSelector } from 'react-redux'
import { setComponentValid } from 'reduxes/slices/invalid/actions'
import { getTimelinesPaging } from 'reduxes/slices/paging/selectors'
import { updateParticipant } from 'reduxes/slices/participants/actions'
import {
  fetchMoreTimelines,
  fetchTimelines,
} from 'reduxes/slices/timelines/actions'
import { getMatchingTimelines } from 'reduxes/slices/timelines/selectors'
import {
  getSelectedTopic
} from 'reduxes/slices/topics/selectors'
import { getTimelineThreadedFrom } from 'reduxes/slices/topics/selectors'

import { css } from 'design'
import { cn } from 'util/cn'

import { useAxios } from '../../hooks/use_axios'
import { useWindowFocus } from '../../hooks/use_window_focus'
import { setDestroyed } from '../../reduxes/slices/api/actions'
import { makeGetStatus } from '../../reduxes/slices/api/selectors'
import {
  getCurrentAccount,
  getCurrentParticipantForSelectedTopic,
} from '../../reduxes/slices/current/selectors'
import { isLiveChatApp } from '../../reduxes/slices/live_chat_app/selectors'
import { getMaxTimelineUpdatedAt } from '../../reduxes/slices/timelines/selectors'
import { useSelectorDelegateStore } from '../../reduxes/stores/root_store/hooks'
import Chat from './items/chat'
import { Lightbox, LightboxContext } from './lightbox'
import { PersonalChatActions } from './personal_chat_actions'
import { TimelineComponent } from './timeline_component'
import { TimelineGroup } from './timeline_group'
import { TimelinesFocusManager } from './timelines_focus_manager'
import { TimelinesInfiniteScroller } from './timelines_infinite_scroller'
import { TimelinesLimitWarning } from './timelines_limit_warning'
import { TimelinesUpgradePrompt } from './timelines_upgrade_prompt'
import { NameAndEmailForm } from './widget/name_and_email_form'

const FORMATTING_NODE_NAMES = ['strong', 'em']

const MemoizedTimelineComponent = React.memo(TimelineComponent)

const createTimelineGroup = (consecutive, groupKey) => {
  if (consecutive.length < 5) {
    return consecutive
  } else {
    return [
      <TimelineGroup key={groupKey} data-timeline-id={groupKey}>
        {consecutive}
      </TimelineGroup>,
    ]
  }
}

export default function Timelines() {
  const dispatch = useDispatch()
  const axios = useAxios()
  const [isFirstFetch, setIsFirstFetch] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [updatingParticipant, setUpdatingParticipant] = useState(false)
  const [failedParticipantIds, setFailedParticipantIds] = useState([])
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  const account = useSelector(getCurrentAccount)
  const selectedTopic = useSelector(getSelectedTopic)

  const timelineThreadedFrom = useSelector(getTimelineThreadedFrom)
  const currentParticipant = useSelector(getCurrentParticipantForSelectedTopic)
  const needsReload = useSelector((state) => state.invalid.timelines)
  const timelines = useSelector(getMatchingTimelines)
  const maxTimelineUpdatedAt = useSelector(getMaxTimelineUpdatedAt)
  const { after, before, hasMore } = useSelector(getTimelinesPaging)
  const {
    unknownContact,
    participantMode,
    showMessages,
    setShowMessages,
    dialogVisible,
    setDialogVisible,
    confirmAction,
    toggleAction,
    dialogAction,
  } = useUnknownContact()
  const windowFocused = useWindowFocus()
  const getStatus = useMemo(makeGetStatus, [])
  const firstStatus = useSelector((state) =>
    getStatus(state, timelines[0]?.tl_id),
  )

  const { id: selectedTopicId, participant_mode } = selectedTopic || {}
  const selectedTopicIdRef = useRef()
  selectedTopicIdRef.current = selectedTopicId

  const scrollBoxRef = useRef(null)

  useEffect(() => {
    setIsFirstFetch(true)
    setIsFetching(false)
  }, [selectedTopicId])

  useEffect(() => {
    const { id, last_activity_at: lastActivityAt } = currentParticipant || {}
    if (
      !selectedTopicId ||
      !id ||
      !windowFocused ||
      updatingParticipant ||
      !maxTimelineUpdatedAt ||
      failedParticipantIds.includes(id)
    ) {
      return
    }
    if (!lastActivityAt || Date.parse(lastActivityAt) < maxTimelineUpdatedAt) {
      setUpdatingParticipant(true)
      dispatch(
        axios(
          updateParticipant(
            selectedTopicId,
            id,
            {},
            {
              update_last_activity_at: true,
            },
          ),
        ),
      )
        .finally(() => {
          setUpdatingParticipant(false)
        })
        .catch(() => {
          setFailedParticipantIds((state) => [...state, id])
        })
    }
  }, [
    axios,
    currentParticipant,
    dispatch,
    failedParticipantIds,
    maxTimelineUpdatedAt,
    selectedTopicId,
    updatingParticipant,
    windowFocused,
  ])

  const onLoadMore = useCallback(() => {
    if (!hasMore || isFetching) {
      return
    }
    setIsFetching(true)
    dispatch(axios(fetchMoreTimelines(selectedTopicId, before))).finally(() => {
      setIsFetching(false)
    })
  }, [axios, before, dispatch, hasMore, isFetching, selectedTopicId])

  useEffect(() => {
    if (!selectedTopicId || isFetching || (!isFirstFetch && !needsReload)) {
      return
    }

    setIsFetching(true)
    dispatch(axios(fetchTimelines(selectedTopicId, after)))
      .finally(() => {
        if (selectedTopicIdRef.current !== selectedTopicId)  {
          return
        }
        setIsFetching(false)
        setIsFirstFetch(false)
        if (needsReload) {
          dispatch(setComponentValid('timelines'))
        }
      })
      .catch((e) => {
        if (e?.response?.status === 403) {
          dispatch(setDestroyed('topics', selectedTopicId))
        } else {
          ErrorBoundary.logErrorToMyService(e)
        }
      })
  }, [
    after,
    axios,
    dispatch,
    isFetching,
    isFirstFetch,
    needsReload,
    selectedTopicId,
  ])

  const invalidateLightbox = useContext(LightboxContext)
  useEffect(() => {
    if (!invalidateLightbox) {
      return
    }

    invalidateLightbox()
  }, [invalidateLightbox, timelines])

  useEffect(() => {
    setShowMessages(false)
  }, [selectedTopicId, setShowMessages])

  const timelineComponents = useMemo(() => {
    let consecutive
    let groupKey
    const timelinesLastIndex = timelines.length - 1
    let previousTimeline
    let lastDay
    let timelineDaySubst = 10
    const processedComponents = []
    if (participant_mode === 'personal') {
      processedComponents.push(
        <PersonalChatActions key="timelines__personal__chat__actions" />,
      )
    }

    if (timelineThreadedFrom) {
      processedComponents.push(
        <Chat
          timeline={timelineThreadedFrom}
          threadedFrom
          key="timelines__timeline__threaded__from"
        />,
      )
    }

    timelines.forEach((timeline, index) => {
      let result = []
      const groupTogether = canGroupTogether(previousTimeline, timeline)
      const currentDay = timeline.created_at.substring(0, timelineDaySubst)
      const sameDay = !lastDay || lastDay === currentDay
      const timelineComponent = (
        <div
          className={cn(
            `topic-elems__container__item--${timeline.tl_type.toLowerCase()}`,
            groupTogether && 'topic-elems__container__item--consecutive',
            timelineItemStyles(),
          )}
          key={timeline.id}
          data-timeline-id={timeline.id}
          role="listitem"
          tabIndex={-1}
        >
          <MemoizedTimelineComponent
            timeline={timeline}
            groupTogether={groupTogether}
          />
        </div>
      )

      let dayComponent = null
      if (!sameDay) {
        dayComponent = (
          <DayDivider
            created_at={timeline.created_at}
            key={`daydivider__${timeline.created_at}`}
          />
        )
      }

      const toAdd = dayComponent
        ? [dayComponent, timelineComponent]
        : [timelineComponent]

      if (timeline.tl_type === 'Status') {
        if (consecutive) {
          consecutive = [...consecutive, ...toAdd]
          if (index === timelinesLastIndex) {
            const group = createTimelineGroup(consecutive, groupKey)
            result = [...result, ...group]
          }
        } else {
          if (index === timelinesLastIndex) {
            result = [...result, ...toAdd]
          } else {
            groupKey = 'group-starting-at-tl-' + timeline.id
            consecutive = toAdd
          }
        }
      } else if (consecutive) {
        const group = createTimelineGroup(consecutive, groupKey)
        result = [...result, ...group, ...toAdd]
        consecutive = undefined
      } else {
        result = [...result, ...toAdd]
      }

      processedComponents.push(result)

      lastDay = currentDay
      previousTimeline = timeline
    })

    if (hasMore === false && participant_mode !== 'personal') {
      if (account?.timelines_limit_at && firstStatus?.message !== 'START') {
        processedComponents.unshift(
          <TimelinesUpgradePrompt
            key={'timelines-upgrade-prompt'}
            data-timeline-id={'timelines-upgrade-prompt'}
          />,
        )
      } else if (account?.timelines_warning_at) {
        processedComponents.unshift(
          <TimelinesLimitWarning
            key={'timelines-limit-warning'}
            data-timeline-id={'timelines-limit-warning'}
          />,
        )
      }
    }

    return processedComponents
  }, [
    account?.timelines_limit_at,
    account?.timelines_warning_at,
    firstStatus?.message,
    hasMore,
    participant_mode,
    timelineThreadedFrom,
    timelines,
  ])

  // Add an event listener to handle when text is copied in the timeline
  // This will strip the content down to just the anchor (usually a text element) and its siblings
  // This is to prevent an issue where the entire element tree is copied as HTML (including action bar buttons, and anchors)
  useEffect(() => {
    const onCopy = () => {
      const initialSelection = window.getSelection()

      // If text nodes are selected (anchorNode and focusNode), then it is a valid text selection
      // NOTE: anchorNode and focusNode will not be null
      const areTextNodesSelected =
        initialSelection.anchorNode.nodeType === 3 &&
        initialSelection.focusNode.nodeType === 3
      if (areTextNodesSelected) {
        return
      }

      // If a formatting node is selected (bold, italic) this generally means it's the first element in
      // a block selection. We need to select 2 parents up to handle this case, otherwise work from the anchorNode
      const isFormattingNode = FORMATTING_NODE_NAMES.includes(
        initialSelection.anchorNode.parentElement.nodeName.toLowerCase(),
      )
      const rootNode = isFormattingNode
        ? initialSelection.anchorNode.parentElement?.parentElement
        : initialSelection.anchorNode

      if (!rootNode) {
        return
      }

      initialSelection.removeAllRanges()
      const range = new Range()
      range.selectNodeContents(rootNode)
      initialSelection.addRange(range)
    }

    document.addEventListener('copy', onCopy)

    return () => {
      document.removeEventListener('copy', onCopy)
    }
  }, [])

  return (
    <>
      <TimelinesFocusManager scrollBoxRef={scrollBoxRef}>
        <MemoizedTimelinesInfiniteScroller
          topicId={selectedTopicId}
          onLoadMore={onLoadMore}
          isFetching={isFetching}
          ref={scrollBoxRef}
          firstFetchComplete={!!before}
        >
          {liveChatApp && (
            <NameAndEmailForm data-timeline-id={'widget-name-and-email-form'} />
          )}
          {!liveChatApp && unknownContact
            ? showMessages && timelineComponents
            : timelineComponents}
          {!liveChatApp &&
            unknownContact &&
            (participantMode === 'direct_chat' ? (
              <UnknownContactPanel
                showMessages={showMessages}
                setShowMessages={setShowMessages}
                toggleAction={toggleAction}
              />
            ) : (
              <UnknownTopicPanel
                showMessages={showMessages}
                setShowMessages={setShowMessages}
                toggleAction={toggleAction}
              />
            ))}
        </MemoizedTimelinesInfiniteScroller>
      </TimelinesFocusManager>

      <SpamOptionsDialog
        showDialog={dialogVisible}
        setDialogVisible={setDialogVisible}
        confirmAction={confirmAction}
        dialogAction={dialogAction}
      />
    </>
  )
}

const timelineItemStyles = css({
  position: 'relative',
  outline: 0,
})

const GROUP_RULES = {
  Chat: ['Chat', 'Task'],
  Task: ['Chat', 'Task'],
}

const canGroupTogether = (previousTimeline, timeline) => {
  if (!previousTimeline) {
    return false
  }
  const sameHandle = previousTimeline.handle === timeline.handle
  const sameGroup = (GROUP_RULES[previousTimeline.tl_type] || []).includes(
    timeline.tl_type,
  )
  const sameTimeWindow =
    previousTimeline.created_at.substring(0, 15) ===
    timeline.created_at.substring(0, 15)
  return sameHandle && sameGroup && sameTimeWindow
}

const MemoizedTimelinesInfiniteScroller = React.memo(TimelinesInfiniteScroller)
